/**
 * Protected Route Component
 * 
 * Wrapper component that protects routes requiring authentication and authorization.
 * Redirects unauthenticated users to the sign-in page while preserving
 * the originally requested URL for post-login redirection.
 * Redirects unauthorized users (without required groups) to appropriate pages.
 * 
 * Requirements: 2.5, 4.4, 10.1, 10.2, 10.3, 10.4
 */

import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAuthorization } from '../contexts/AuthorizationContext';

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requiredGroups?: string[];
}

/**
 * ProtectedRoute Component
 * 
 * Checks authentication and authorization status before rendering children.
 * - If loading: Shows loading indicator
 * - If not authenticated: Redirects to sign-in page with original URL in state
 * - If authenticated but no groups: Redirects to pending approval page
 * - If authenticated but lacks required groups: Shows access denied message and redirects to full status
 * - If authenticated and authorized: Renders the protected content
 * 
 * @param children - The protected content to render when authenticated and authorized
 * @param redirectTo - The path to redirect to when not authenticated (default: '/signin')
 * @param requiredGroups - Optional array of group names required to access this route
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  redirectTo = '/signin',
  requiredGroups
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { hasGroup, canAccess, groupsLoading } = useAuthorization();
  const location = useLocation();

  const hasRequiredGroups = Boolean(requiredGroups && requiredGroups.length > 0);
  // True once we know the user is authenticated, has some group, but not one
  // of the groups required for this route.
  const isAccessDenied =
    hasRequiredGroups && !groupsLoading && hasGroup && !canAccess(requiredGroups!);

  const [redirectAfterDenied, setRedirectAfterDenied] = useState(false);

  // Show the "Access Denied" message for a couple seconds before navigating
  // away. Scheduling the redirect in an effect (rather than mutating state
  // during render) ensures the message actually paints instead of being
  // superseded by a same-pass re-render before React ever commits it.
  useEffect(() => {
    if (!isAccessDenied) {
      setRedirectAfterDenied(false);
      return;
    }
    const timer = setTimeout(() => setRedirectAfterDenied(true), 2000);
    return () => clearTimeout(timer);
  }, [isAccessDenied]);

  // Show loading indicator while checking authentication status
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div role="progressbar" className="w-8 h-8 border-4 border-[var(--primary-200)] border-t-[var(--primary-600)] rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to sign-in if not authenticated, storing the original URL
  if (!isAuthenticated) {
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  // If no requiredGroups specified, only check authentication (backward compatible)
  if (!hasRequiredGroups) {
    return <>{children}</>;
  }

  // Groups are still being extracted from the token (e.g. right after a page
  // refresh). Wait for extraction to complete instead of evaluating
  // hasGroup/canAccess against a temporarily-empty groups array, which would
  // otherwise cause a brief, incorrect redirect to /pending-approval.
  if (groupsLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div role="progressbar" className="w-8 h-8 border-4 border-[var(--primary-200)] border-t-[var(--primary-600)] rounded-full animate-spin" />
      </div>
    );
  }

  // If user has no groups at all, redirect to pending approval page
  if (!hasGroup) {
    return <Navigate to="/pending-approval" replace />;
  }

  // If user has groups but not the required ones, show access denied and redirect
  if (isAccessDenied) {
    if (!redirectAfterDenied) {
      return (
        <div className="flex flex-col justify-center items-center min-h-screen gap-4">
          <div className="bg-[#fee2e2] border-l-[3px] border-[var(--destructive)] rounded-lg px-4 py-3 text-sm text-[#991b1b]">
            Access Denied: You do not have permission to access this page. Redirecting...
          </div>
        </div>
      );
    }

    // After showing the message, redirect
    return <Navigate to="/" replace />;
  }

  // Render protected content if authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;
